/**
 * 写字模块的内置字表和字形（按需加载）。
 * 数据直接引用 iOS 写字模块的 Resources/HanziData（两端唯一的数据源，见 AGENTS.md，不复制一份）：
 * - lexicon.json：一年级上册写字表的拼音、笔顺名称、组词（约 11 KB）
 * - graphics.json：hanzi-writer-data 字形（约 130 KB，Arphic Public License）
 * 构建时作为单独的懒加载分块，只有打开写字编辑器 / 预览时才下载。
 */
import { useEffect, useState } from "react";

export interface LexiconEntry {
  pinyin: string;
  stroke_names: string[];
  words: string[];
}

export interface GlyphData {
  /** makemeahanzi 坐标（1024×1024，y 轴向上，基线 y = -124） */
  strokes: string[];
  medians: number[][][];
}

export interface HanziData {
  lexicon: Record<string, LexiconEntry>;
  graphics: Record<string, GlyphData>;
}

let loading: Promise<HanziData> | null = null;
let loaded: HanziData | null = null;

export function loadHanziData(): Promise<HanziData> {
  if (!loading) {
    loading = Promise.all([
      import("../../../../ios/Packages/KidsKit/Sources/Handwriting/Resources/HanziData/lexicon.json?raw"),
      import("../../../../ios/Packages/KidsKit/Sources/Handwriting/Resources/HanziData/graphics.json?raw"),
    ])
      .then(([lex, gfx]) => {
        loaded = { lexicon: JSON.parse(lex.default) as HanziData["lexicon"], graphics: JSON.parse(gfx.default) as HanziData["graphics"] };
        return loaded;
      })
      .catch((e: unknown) => {
        // 加载失败时允许下次重试；拼音 / 组词由家长手动填写
        loading = null;
        console.warn("[learn] 字表加载失败", e);
        return { lexicon: {}, graphics: {} };
      });
  }
  return loading;
}

/** 字表数据（加载完成前为 null） */
export function useHanziData(): HanziData | null {
  const [data, setData] = useState<HanziData | null>(loaded);
  useEffect(() => {
    if (data) return;
    let alive = true;
    void loadHanziData().then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, [data]);
  return data;
}

/** 笔顺名称：字表里有且数量和字形一致就用字表的，否则用"第 N 笔"（与 iOS HanziLibrary.strokeNames 一致） */
export function strokeNames(data: HanziData, ch: string): string[] {
  const n = data.graphics[ch]?.strokes.length ?? 0;
  const names = data.lexicon[ch]?.stroke_names;
  if (names && names.length === n) return names;
  return Array.from({ length: n }, (_, i) => `第 ${i + 1} 笔`);
}

/** 字形（SVG）。没有字形数据时显示普通文字 */
export function HanziGlyph({ data, char, size = 44, color = "var(--kp-ink)" }: { data: HanziData | null; char: string; size?: number; color?: string }) {
  const g = data?.graphics[char];
  if (!g || !g.strokes.length) {
    return (
      <span className="learn-glyph-text" style={{ width: size, height: size, fontSize: size * 0.8, color }}>
        {char}
      </span>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 1024 1024" aria-label={char} role="img">
      {/* makemeahanzi 的 y 轴向上：翻转后下移 900 */}
      <g transform="matrix(1 0 0 -1 0 900)" fill={color}>
        {g.strokes.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
    </svg>
  );
}
